import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import AddButton from 'components/v2/buttons/AddButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import DropdownMenu from 'components/v2/dropdowns/DropdownV2';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import Pagination from 'components/v2/PaginationV2';
import TextField from 'components/v2/forms/TextField';
import Table from 'components/v2/Table';
import DotsHorizontalButton from 'components/v2/buttons/DotsHorizontalButton';
import { useGetMarketShipperRates } from 'utils/MarketRatesHooks';
import formatMoney from 'utils/formatters/Money';
import { showToast } from 'stores/ToastStore';
import { deleteMarketRate, updateMarketShipperRate, } from 'utils/PricingWebAPIUtils';
import CustomerSearch from 'components/site/customer-search';
import { getShipper } from 'utils/CustomersAPIUtils';
import { unescapeHTML } from 'underscore.string';
import { withYardLayout } from './YardLayout';
import { ITEMS_PER_PAGE_LIMIT } from './constants';
function MarketRates(props) {
    const pageLimit = ITEMS_PER_PAGE_LIMIT;
    const yard = props.yard;
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [activePage, setActivePage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedRate, setSelectedRate] = useState(null);
    const emptyMarketRate = {
        shipper: null,
        rate: null,
        freeDays: null,
        shipperId: null,
    };
    const [newMarketRate, setNewMarketRate] = useState(emptyMarketRate);
    const [autocompleteText, setAutocompleteText] = useState('');
    useEffect(() => {
        (async () => {
            await setPage(1);
        })();
        return () => {
            setData(null);
            setLoading(true);
        };
    }, []);
    const setPage = async (page, pageItemsLimit = pageLimit) => {
        setActivePage(page);
        setLoading(true);
        const offset = (page - 1) * pageItemsLimit;
        const marketRatesResponse = await useGetMarketShipperRates(yard.marketId, pageItemsLimit, offset);
        if (marketRatesResponse.rates) {
            const ratesWithShipper = await Promise.all(marketRatesResponse.rates.rows.map(async (row) => {
                try {
                    const shipper = await getShipper(row.shipperId);
                    return Object.assign(Object.assign({}, row), { shipperName: unescapeHTML(shipper.entity.account_name || '').trim() });
                }
                catch (_a) {
                    console.error(`Couldn't find shipper with id ${row.shipperId}`);
                }
            }));
            setData(ratesWithShipper.filter((item) => item));
            setTotal(marketRatesResponse.total);
            setLoading(false);
        }
        else {
            console.log(marketRatesResponse.error);
        }
    };
    function handleNewMarketRateAdd(field, value, isNumber = false, allowDecimal = false) {
        setNewMarketRate((oldMarketRate) => {
            if (isNumber) {
                oldMarketRate[field] = allowDecimal
                    ? Math.round(value * 100) / 100
                    : parseInt(value, 10);
            }
            else {
                oldMarketRate[field] = value;
            }
            return Object.assign({}, oldMarketRate);
        });
    }
    const handleShipperFilterSelect = (_, item) => {
        handleNewMarketRateAdd('shipper', item.name);
        handleNewMarketRateAdd('shipperId', item._id);
        setAutocompleteText(item.name);
    };
    async function handleAddClick(e) {
        e.preventDefault();
        try {
            setLoading(true);
            await updateMarketShipperRate({
                marketId: yard.marketId,
                shipperId: newMarketRate.shipperId,
                rate: newMarketRate.rate,
                freeDays: newMarketRate.freeDays,
            });
            showToast(t('MARKET_RATE_ADDED_SUCCESSFULLY'), t('MARKET_RATE_ADDED_SUCCESSFULLY'), 'success');
            await setPage(activePage);
            setNewMarketRate(Object.assign({}, emptyMarketRate));
            setAutocompleteText('');
            setTotal(total + 1);
        }
        catch (e) {
            showToast(t('ADD_MARKET_RATE_ERROR'), t('ADD_MARKET_RATE_FAILED'), 'error');
            return;
        }
        finally {
            setLoading(false);
        }
    }
    function handleShowDeleteMode(rate) {
        return async () => {
            try {
                setLoading(true);
                await deleteMarketRate(rate.id);
                showToast(t('MARKET_RATE_DELETE_SUCCESSFULLY'), t('MARKET_RATE_DELETE_SUCCESSFULLY'), 'success');
                const newTotal = total - 1;
                setTotal(newTotal);
                const newMaxPage = Math.ceil(newTotal / pageLimit);
                if (activePage > newMaxPage) {
                    await setPage(Math.max(newMaxPage, 1));
                }
                else {
                    await setPage(activePage);
                }
            }
            catch (e) {
                showToast(t('DELETE_MARKET_RATE_ERROR'), t('DELETE_MARKET_RATE_FAILED'), 'error');
                return;
            }
            finally {
                setLoading(false);
            }
        };
    }
    function handleShowEditMode(rate) {
        return function () {
            const selectedRate = Object.assign({}, rate);
            setSelectedRate(selectedRate);
        };
    }
    async function handelApproveEdit() {
        try {
            await updateMarketShipperRate({
                id: selectedRate.id,
                marketId: yard.marketId,
                shipperId: selectedRate.shipperId,
                rate: selectedRate.rate,
                freeDays: selectedRate.freeDays,
            });
            showToast(t('MARKET_RATE_UPDATED_SUCCESSFULLY'), t('MARKET_RATE_UPDATED_SUCCESSFULLY'), 'success');
        }
        catch (e) {
            showToast(t('UPDATE_MARKET_RATE_ERROR'), t('UPDATE_MARKET_RATE_FAILED'), 'error');
            return;
        }
        finally {
            setSelectedRate(null);
        }
        setData((oldData) => {
            const updatedData = oldData.map((item) => {
                if (item.id === selectedRate.id) {
                    item = selectedRate;
                }
                return item;
            });
            return [...updatedData];
        });
    }
    function handleCancelEdit() {
        setSelectedRate(null);
    }
    function handleSelectedRateUpdate(field, value, isNumber = false, allowDecimal = false) {
        setSelectedRate((oldSelectedRate) => {
            const updatedSelectedRate = oldSelectedRate;
            if (isNumber) {
                updatedSelectedRate[field] = allowDecimal
                    ? Math.round(value * 100) / 100
                    : parseInt(value, 10);
            }
            else {
                updatedSelectedRate[field] = value;
            }
            return Object.assign({}, updatedSelectedRate);
        });
    }
    function handlePageChange(page) {
        setPage(page);
    }
    function isShipperInputValid(value) {
        var _a;
        return ((_a = value === null || value === void 0 ? void 0 : value.trim()) === null || _a === void 0 ? void 0 : _a.length) > 0;
    }
    function isRateInputValid(value) {
        return value > 0;
    }
    function isFreeDaysInputValid(value) {
        return value >= 0 && value < 100;
    }
    function isEditing(row) {
        return selectedRate && selectedRate.id === row.id;
    }
    function renderEditMenuButon(row) {
        return (React.createElement("div", { "data-testid": `editMenuMarketRateBtnWrapper-${row.id}` },
            React.createElement(DotsHorizontalButton, { id: `actions-button-${row.id}`, className: "transform rotate-90" })));
    }
    function renderTableHeader(header) {
        return React.createElement("div", { className: "text-sm font-semibold" }, header.label);
    }
    const headers = [
        {
            field: 'Shipper',
            label: t('SHIPPER'),
            renderHeader: renderTableHeader,
        },
        {
            field: 'Rate',
            label: t('RATE'),
            renderHeader: renderTableHeader,
        },
        {
            field: 'Free Days',
            label: t('FREE_DAYS'),
            renderHeader: renderTableHeader,
        },
    ];
    const columns = [
        {
            field: 'shipper',
            render: (shipper, row) => {
                if (isEditing(row) && !selectedRate.shipperId) {
                    // update this to just show shipper name on edit, but should still be a customer search on "edit" of a brand new rate
                    return (React.createElement(CustomerSearch, { clearOnSelect: false, onSelect: handleShipperFilterSelect, defaultValue: selectedRate.shipperId, value: selectedRate.shipperId, placeholder: t('SEARCH_SHIPPER'), type: "shippers" }));
                }
                return React.createElement("span", null, row.shipperName);
            },
        },
        {
            field: 'rate',
            render: (rate, row) => {
                if (isEditing(row)) {
                    return (React.createElement(TextField, { name: "rate", type: "number", className: !isRateInputValid(selectedRate.rate) ? 'error' : '', value: selectedRate.rate, onChange: (value) => handleSelectedRateUpdate('rate', value, true, true) }));
                }
                return React.createElement("span", null, rate ? formatMoney(rate) : '-');
            },
        },
        {
            field: 'freeDays',
            render: (freeDays, row) => {
                if (isEditing(row)) {
                    return (React.createElement(TextField, { name: "freeDays", type: "number", min: 0, max: 100, className: !isFreeDaysInputValid(selectedRate.freeDays) ? 'error' : '', value: selectedRate.freeDays, onChange: (value) => handleSelectedRateUpdate('freeDays', value, true) }));
                }
                return React.createElement("span", null, freeDays);
            },
        },
        {
            field: 'actions',
            render: (_, row) => {
                if (isEditing(row)) {
                    return (React.createElement("div", { className: "flex items-center space-x-2 justify-end" },
                        React.createElement(ApproveButton, { dataTestId: "approveMarketRate", onClick: handelApproveEdit, disabled: 
                            // add this back once customer update selector is added/fixed
                            // !isShipperInputValid(selectedRate.shipper) ||
                            !isRateInputValid(selectedRate.rate) ||
                                !isFreeDaysInputValid(selectedRate.freeDays) }),
                        React.createElement(DeclineButton, { dataTestId: "declineMarketRate", color: "gray", onClick: handleCancelEdit })));
                }
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement(DropdownMenu, { useIcon: true },
                        React.createElement(Menu.Item, { key: "edit_rate" },
                            React.createElement(DropdownItem, { dataTestId: "editMarketRateButton", onClick: handleShowEditMode(row) }, t('EDIT_RATE'))),
                        React.createElement(Menu.Item, { key: "delete_rate" },
                            React.createElement(DropdownItem, { dataTestId: "deleteMarketRateButton", onClick: handleShowDeleteMode(row) }, t('DELETE_RATE'))))));
            },
        },
    ];
    function renderFooter() {
        return (React.createElement(React.Fragment, null,
            React.createElement("tr", null,
                React.createElement("td", null,
                    React.createElement(CustomerSearch, { clearOnSelect: false, onSelect: handleShipperFilterSelect, value: autocompleteText, placeholder: t('SEARCH_SHIPPER'), type: "shippers" })),
                React.createElement("td", null,
                    React.createElement(TextField, { name: "rate", type: "number", className: newMarketRate.rate && !isRateInputValid(newMarketRate.rate)
                            ? 'error'
                            : '', placeholder: t('RATE'), value: newMarketRate.rate || '', onChange: (value) => handleNewMarketRateAdd('rate', value, true, true) })),
                React.createElement("td", null,
                    React.createElement(TextField, { name: "freeDays", type: "number", min: 0, max: 100, className: newMarketRate.freeDays &&
                            !isFreeDaysInputValid(newMarketRate.freeDays)
                            ? 'error'
                            : '', placeholder: t('FREE_DAYS'), value: newMarketRate.freeDays !== null ? newMarketRate.freeDays : '', onChange: (value) => handleNewMarketRateAdd('freeDays', value, true) })),
                React.createElement("td", null,
                    React.createElement("div", { className: "flex justify-end" },
                        React.createElement(AddButton, { onClick: handleAddClick, disabled: !isShipperInputValid(newMarketRate.shipper) ||
                                !isRateInputValid(newMarketRate.rate) ||
                                !isFreeDaysInputValid(newMarketRate.freeDays) })))),
            React.createElement("tr", null,
                React.createElement("td", { colSpan: 4, className: "p-0" },
                    React.createElement(Pagination, { className: "border-t-0", perPage: pageLimit, page: activePage, pages: Math.ceil(total / pageLimit), total: total, showCount: true, paginationContainerClassName: "mx-auto my-0 sticky", onPageClick: (page) => handlePageChange(page) })))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", { className: "mb-4" }, t('MARKET_RATE_other', { count: 3 })),
        loading ? (React.createElement("h4", null, t('LOADING'))) : (React.createElement(Table, { headers: headers, columns: columns, data: data, footer: renderFooter, className: "mb-36" }))));
}
export default withYardLayout('rates', MarketRates);
